// Contas puras do "setor do custo" (sem antd, para o jest do CRA conseguir carregar).
// O componente SeletorSetores, a ficha de RH, os kits e a Margem por setor importam daqui.
import { SETORES } from '../../financeiro-contas-receber/utils';

export type FracoesSetores = Record<string, number>;

/** Frações iguais (1/n, 4 casas) para as chaves dadas; chaves inválidas e repetidas saem. */
export function fracoesIguais(chaves: string[]): FracoesSetores | null {
  const unicas = Array.from(new Set(chaves.filter((c) => SETORES[c])));
  if (!unicas.length) return null;
  const f = Math.round((1 / unicas.length) * 10000) / 10000;
  const out: FracoesSetores = {};
  unicas.forEach((c) => { out[c] = f; });
  // 1/3 em 4 casas não soma 1: o resto vai para o último, para a soma fechar em 1.
  const soma = f * (unicas.length - 1);
  out[unicas[unicas.length - 1]] = Math.round((1 - soma) * 10000) / 10000;
  return out;
}

/** Setores em ordem canônica (a de SETORES); chave desconhecida vai para o fim. */
export function chavesOrdenadas(s: FracoesSetores | null | undefined): string[] {
  return Object.keys(s || {}).sort((a, b) => (SETORES[a]?.ordem ?? 99) - (SETORES[b]?.ordem ?? 99));
}

const pct = (f: number) => `${Math.round((Number(f) || 0) * 100)}%`;
const nomeSetor = (c: string) => SETORES[c]?.rotulo || c;

/** "Topografia 50% · Hidrometria 50%"; "—" quando não há setor. */
export function rotuloSetores(s: FracoesSetores | null | undefined): string {
  const cs = chavesOrdenadas(s);
  if (!cs.length) return '—';
  return cs.map((c) => `${nomeSetor(c)} ${pct((s as FracoesSetores)[c])}`).join(' · ');
}

/** Só os nomes, curtos, para a tabela de pendências ("Topografia, Hidrometria"). */
export function nomesSetores(s: FracoesSetores | null | undefined): string {
  const cs = chavesOrdenadas(s);
  return cs.length ? cs.map(nomeSetor).join(', ') : '—';
}

/** Os dois mapas têm as mesmas chaves e frações (tolerância de arredondamento)? Decide "mudou". */
export function mesmosSetores(a: FracoesSetores | null | undefined, b: FracoesSetores | null | undefined): boolean {
  const ka = chavesOrdenadas(a), kb = chavesOrdenadas(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k, i) => k === kb[i] && Math.abs((a as FracoesSetores)[k] - (b as FracoesSetores)[k]) < 0.0005);
}

export interface ViasCusto { item: number; contrato: number; sem_setor: number; sem_casamento: number }

const reaisInteiro = (v: number) => `R$ ${Math.round(Number(v) || 0).toLocaleString('pt-BR')}`;

/** Linha de texto do mês: quanto do custo direto foi repartido por cada via. '' se não há vias. */
export function resumoVias(v: Partial<ViasCusto> | null | undefined): string {
  if (!v) return '';
  const n = (x: any) => Number(x) || 0;
  const item = n(v.item), contrato = n(v.contrato), semSetor = n(v.sem_setor), semCas = n(v.sem_casamento);
  if (item + contrato + semSetor + semCas < 0.5) return '';
  return `${reaisInteiro(item)} pelo setor do custo · ${reaisInteiro(contrato + semSetor + semCas)} pela mistura `
    + `(${reaisInteiro(semSetor)} sem setor, ${reaisInteiro(semCas)} sem casamento)`;
}

/** Soma as vias de vários meses (para a linha do período). */
export function somarVias(lista: (Partial<ViasCusto> | null | undefined)[]): ViasCusto {
  const t: ViasCusto = { item: 0, contrato: 0, sem_setor: 0, sem_casamento: 0 };
  lista.forEach((v) => {
    if (!v) return;
    t.item += Number(v.item) || 0; t.contrato += Number(v.contrato) || 0;
    t.sem_setor += Number(v.sem_setor) || 0; t.sem_casamento += Number(v.sem_casamento) || 0;
  });
  return t;
}
